import type { NoDoRankingUtm } from "@base/contracts";
import { cn, Dica } from "@base/ui";
import { ChevronDown } from "lucide-react";
import { EXPLICACAO_DO_PAINEL } from "../domain/explicacoes";
import { formatar } from "../domain/formatadores";

const ROTULO: Readonly<Record<NoDoRankingUtm["nivel"], string>> = {
  campanha: "Campanha",
  conjunto: "Conjunto",
  anuncio: "Anúncio",
};

export function RankingDeUtm({ ranking }: { readonly ranking: readonly NoDoRankingUtm[] }) {
  const teto = Math.max(1, ...ranking.map((item) => (item.vendas > 0 ? item.vendas : item.oportunidades)));

  return (
    <aside
      aria-labelledby="titulo-ranking-utm"
      className="flex h-[28rem] flex-col overflow-hidden rounded-3xl bg-card shadow-sm ring-1 ring-border/70"
    >
      <div className="flex shrink-0 items-start justify-between gap-2 border-b border-border/70 px-5 py-4">
        <div className="flex flex-col gap-1">
          <h3 id="titulo-ranking-utm" className="text-base font-medium tracking-tight">
            Ranking por UTM
          </h3>
          <p className="text-xs text-muted-foreground">Abra a campanha, depois o conjunto.</p>
        </div>
        <Dica rotulo="Como o ranking por UTM é calculado">{EXPLICACAO_DO_PAINEL.utm}</Dica>
      </div>

      {ranking.length === 0 ? (
        <p className="px-5 py-8 text-sm text-muted-foreground">Sem UTM neste recorte.</p>
      ) : (
        <ol className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
          {ranking.map((item, indice) => (
            <ItemDoRanking key={`${item.nivel}-${item.nome ?? "sem"}`} item={item} indice={indice + 1} teto={teto} />
          ))}
        </ol>
      )}
    </aside>
  );
}

function ItemDoRanking({ item, indice, teto }: { readonly item: NoDoRankingUtm; readonly indice: number; readonly teto: number }) {
  const volume = item.vendas > 0 ? item.vendas : item.oportunidades;
  const largura = Math.max(6, Math.round((volume / teto) * 100));
  const tetoFilho = Math.max(1, ...item.filhos.map((filho) => (filho.vendas > 0 ? filho.vendas : filho.oportunidades)));
  const linha = <Linha item={item} indice={indice} largura={largura} expansivel={item.filhos.length > 0} />;

  if (item.filhos.length === 0) {
    return <li>{linha}</li>;
  }

  return (
    <li>
      <details className="group">
        <summary className="list-none marker:content-none [&::-webkit-details-marker]:hidden">{linha}</summary>
        <ol className="ml-7 mt-1 space-y-1 border-l border-border/70 pl-3">
          {item.filhos.map((filho, i) => (
            <ItemDoRanking key={`${filho.nivel}-${filho.nome ?? "sem"}`} item={filho} indice={i + 1} teto={tetoFilho} />
          ))}
        </ol>
      </details>
    </li>
  );
}

function Linha({
  item,
  indice,
  largura,
  expansivel,
}: {
  readonly item: NoDoRankingUtm;
  readonly indice: number;
  readonly largura: number;
  readonly expansivel: boolean;
}) {
  const nome = item.nome ?? `Sem ${ROTULO[item.nivel].toLowerCase()}`;
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-2xl px-2 py-2 hover:bg-muted/50",
        expansivel && "cursor-pointer",
      )}
    >
      {expansivel ? (
        <ChevronDown
          aria-hidden="true"
          className="mt-1 size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
        />
      ) : (
        <span className="mt-1 size-4 shrink-0" aria-hidden="true" />
      )}
      <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-muted text-[11px] tabular-nums text-muted-foreground">
        {indice}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{ROTULO[item.nivel]}</p>
        <p className="truncate text-sm font-medium" title={nome}>
          {nome}
        </p>
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted" aria-hidden="true">
          <div className="h-full rounded-full bg-primary" style={{ width: `${largura}%` }} />
        </div>
      </div>
      <div className="shrink-0 pt-0.5 text-right tabular-nums">
        <p className="text-xs font-medium">
          {formatar.quantidade(item.vendas)} {item.vendas === 1 ? "venda" : "vendas"}
        </p>
        <p className="text-[11px] text-muted-foreground">
          {formatar.porcentagem(item.taxa)} · {formatar.quantidade(item.oportunidades)}
        </p>
      </div>
    </div>
  );
}
